/**
 * useProjectStore hook — wires Convex subscriptions to the project store
 * and connects the store's sync function to the Convex dispatch mutation.
 *
 * Usage: call once at the project page level with the workflow ID.
 * All child components use useProjectStore directly for reads and writes.
 */

import { useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'
import type { Patch } from 'mutative'
import { useProjectStore, PENDING_TTL, type EditSource } from '@/lib/projectStore'
import { convexToProjectState, patchesToConvex, resolveArrayPatches } from '@/lib/projectSync'

export function useProjectStoreSync(workflowId: Id<"workflows"> | undefined) {
  const store = useProjectStore
  const hydrated = useProjectStore((s) => s._hydrated)
  const workflowIdRef = useRef(workflowId)
  workflowIdRef.current = workflowId

  // Convex subscriptions
  const workflow = useQuery(
    api.workflows.get,
    workflowId ? { id: workflowId } : 'skip',
  )
  const clips = useQuery(
    api.workflows.getClips,
    workflowId ? { workflowId } : 'skip',
  )

  // Persisted edit history (loaded once on hydration)
  const editHistory = useQuery(
    api.editHistory.getHistory,
    workflowId ? { workflowId } : 'skip',
  )
  const actionHistory = useQuery(
    api.agentHistory.list,
    workflowId ? { workflowId } : 'skip',
  )

  // Convex dispatch mutation
  const dispatchMutation = useMutation(api.editHistory.dispatch)

  // Sync function: called by store on every edit, undo, redo
  const syncFn = useCallback(
    (patches: Patch[], inversePatches: Patch[], label: string, source: EditSource) => {
      const wId = workflowIdRef.current
      if (!wId) return

      // Resolve array patches (audioTracks etc → full array)
      const currentState = store.getState()
      const resolved = resolveArrayPatches(patches, currentState)

      // Convert to Convex table patches
      const tablePatchList = patchesToConvex(resolved, wId)
      if (tablePatchList.length === 0) return

      // Undo/redo syncs should not create new history entries
      const isUndoRedo = label.startsWith('Undo:') || label.startsWith('Redo:')

      // Fire-and-forget dispatch to Convex (with inverse patches for persistent undo)
      dispatchMutation({
        workflowId: wId,
        patches: tablePatchList,
        label,
        source,
        forwardPatches: isUndoRedo ? undefined : patches.map((p) => ({
          op: p.op,
          path: p.path,
          value: p.value,
        })),
        inversePatches: isUndoRedo ? undefined : inversePatches.map((p) => ({
          op: p.op,
          path: p.path,
          value: p.value,
        })),
        skipHistory: isUndoRedo,
      }).catch((err: Error) => {
        console.error('[projectStore] dispatch failed:', err)
      })
    },
    [dispatchMutation, store],
  )

  // Wire sync function once
  useEffect(() => {
    store.getState().setSyncFn(syncFn)
    return () => store.getState().setSyncFn(null as any)
  }, [store, syncFn])

  // Initial hydration + ongoing server sync
  useEffect(() => {
    if (!workflow || !clips || !workflowId) return

    const projectState = convexToProjectState(workflow, clips)

    if (!hydrated) {
      // First load: full hydration
      store.getState().hydrate(projectState)
    } else {
      // Subsequent updates: merge with pending tracking
      store.getState().applyServerState(
        projectState.workflow,
        projectState.clips,
        projectState.clipOrder,
      )
    }
  }, [store, workflow, clips, workflowId, hydrated])

  useEffect(() => {
    if (!actionHistory || !hydrated) return
    store.getState().bridgeServerEntries(actionHistory.map((e) => ({
      id: e.id,
      label: e.label,
      source: e.source as 'user' | 'agent',
      undoOps: e.undoOps,
      undone: e.undone,
      invalidatedAt: e.invalidatedAt,
      createdAt: e.createdAt,
    })))
  }, [actionHistory, hydrated, store])

  // Load persisted edit history into undo stack after hydration
  const historyLoaded = useRef(false)
  useEffect(() => {
    if (!editHistory || actionHistory === undefined || actionHistory.length > 0 || !hydrated || historyLoaded.current) return
    historyLoaded.current = true

    // Convert Convex editHistory records into undo stack entries
    // Filter: must have inverse patches, and must not be undo/redo syncs
    const entries = editHistory
      .filter((e: any) => e.inversePatchData && e.forwardPatchData && !e.label.startsWith('Undo:') && !e.label.startsWith('Redo:'))
      .map((e: any) => {
        try {
          return {
            id: e._id,
            label: e.label,
            source: e.source as 'user' | 'agent',
            patches: JSON.parse(e.forwardPatchData),
            inversePatches: JSON.parse(e.inversePatchData),
            timestamp: e.createdAt,
          }
        } catch {
          return null
        }
      })
      .filter(Boolean) as Array<{ id: string; label: string; source: 'user' | 'agent'; patches: Patch[]; inversePatches: Patch[]; timestamp: number }>

    if (entries.length > 0) {
      store.getState().loadHistory(entries)
    }
  }, [editHistory, actionHistory, hydrated, store])

  // After pending entries expire, re-apply server state so server-managed
  // fields (videoStatus, videoUrl) overwrite any stale undo/redo values.
  const hasPending = useProjectStore((s) => Object.keys(s._pending).length > 0)
  useEffect(() => {
    if (!hasPending || !workflow || !clips || !workflowId) return
    const timer = setTimeout(() => {
      const projectState = convexToProjectState(workflow, clips)
      store.getState().applyServerState(
        projectState.workflow,
        projectState.clips,
        projectState.clipOrder,
      )
    }, PENDING_TTL + 200)
    return () => clearTimeout(timer)
  }, [hasPending, store, workflow, clips, workflowId])

  // Re-hydrate when switching projects
  const prevWorkflowId = useRef(workflowId)
  useEffect(() => {
    if (workflowId && workflowId !== prevWorkflowId.current) {
      prevWorkflowId.current = workflowId
      historyLoaded.current = false
      store.getState().hydrate({
        workflow: {
          _id: workflowId,
          title: '',
          narrative: '',
          characters: [],
          settings: {},
          audioTracks: [],
          textOverlays: [],
        },
        clips: {},
        clipOrder: [],
      })
    }
  }, [store, workflowId])

  return { hydrated, store }
}

/**
 * Keyboard shortcut handler for undo/redo.
 * Call at the project page level.
 */
export function useUndoRedoShortcuts() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey
      if (!isMod || e.key.toLowerCase() !== 'z') return

      // Don't capture when editing text inputs
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return
      }

      e.preventDefault()
      const s = useProjectStore.getState()
      if (e.shiftKey) {
        s.redo()
      } else {
        s.undo()
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])
}
